import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { SnowplowVideoTracker } from '../../Analytics/Snowplow/SnowplowVideoTracker';
import { videoTypes } from '../../../utilities/ui_utilities';

const YoutubePlayerIFrameAPI = ({ autoPlay, videoSrc, videoPlayMetric }) => {
    const playerContainerRef = useRef(null);
    const [player, setPlayer] = useState(null);
    const [hasLogged, setHasLogged] = useState(false);

    function getYoutubeVideoId(url) {
        // regExpo to get video Id from all variants of youtube links.
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        if (match && match[2].length === 11) {
            return match[2];
        }
        return null;
    }

    const emitVideoActionMetrics = (videoAction) => {
        if (videoPlayMetric) {
            SnowplowVideoTracker({
                ...videoPlayMetric,
                videoAction,
                providerName: videoTypes.YOUTUBE,
            });
        }
    };

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !hasLogged) {
                    setHasLogged(true);
                    observer.unobserve(entry.target);
                }
            });
        });

        if (playerContainerRef.current) {
            observer.observe(playerContainerRef.current);
        }

        window.onYouTubeIframeAPIReady = () => {
            const newPlayer = new window.YT.Player(playerContainerRef.current, {
                height: '100%',
                width: '100%',
                videoId: getYoutubeVideoId(videoSrc),
                playerVars: {
                    mute: autoPlay ? 1 : 0,
                    autoplay: autoPlay ? 1 : 0,
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.PLAYING) {
                            emitVideoActionMetrics('play');
                        } else if (
                            event.data === window.YT.PlayerState.PAUSED
                        ) {
                            emitVideoActionMetrics('pause');
                        }
                    },
                },
            });
            setPlayer(newPlayer);
        };

        if (!window.YT) {
            const script = document.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            document.body.appendChild(script);
        } else {
            window.onYouTubeIframeAPIReady();
        }

        return () => {
            if (player) {
                player.destroy();
            }
            if (playerContainerRef.current) {
                observer.unobserve(playerContainerRef.current);
            }
        };
    }, [videoSrc, hasLogged]);

    return <div ref={playerContainerRef} className="youtube-video" />;
};

export default YoutubePlayerIFrameAPI;

YoutubePlayerIFrameAPI.propTypes = {
    autoPlay: PropTypes.bool,
    videoSrc: PropTypes.string.isRequired,
    videoPlayMetric: PropTypes.objectOf(PropTypes.string),
};

YoutubePlayerIFrameAPI.defaultProps = {
    autoPlay: false,
    videoPlayMetric: null,
};
